import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useWalletConnect } from '@btc-vision/walletconnect';
import { Campaign, EXAMPLE_CAMPAIGN } from './config/campaigns';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { StatsBar } from './components/StatsBar';
import { CampaignGrid } from './components/CampaignGrid';
import { HowItWorks } from './components/HowItWorks';
import { CTASection } from './components/CTASection';
import { DonateModal, DonationResult } from './components/DonateModal';
import { DonationRecord } from './config/donations';
import { CreateCampaignModal } from './components/CreateCampaignModal';
import { DonationHistory } from './components/DonationHistory';
import { MyCampaigns } from './components/MyCampaigns';
import { Footer } from './components/Footer';
import { useScrollAnimation } from './hooks/useScrollAnimation';
import { useBitFund } from './hooks/useBitFund';

export function App() {
    const { walletAddress, address } = useWalletConnect();
    const { campaigns, donations, totalRaisedBTC, loading, refresh } = useBitFund();
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDonationHistory, setShowDonationHistory] = useState(false);
    const [showMyCampaigns, setShowMyCampaigns] = useState(false);
    const [pendingDonations, setPendingDonations] = useState<DonationRecord[]>([]);

    const walletHex = useMemo(() => {
        if (!address) return '';
        return '0x' + address.toBigInt().toString(16).padStart(64, '0');
    }, [address]);

    // Clean up pending donations once they appear on-chain
    useEffect(() => {
        if (pendingDonations.length === 0 || donations.length === 0) return;
        const onChainTxIds = new Set(donations.filter((d) => d.txId).map((d) => d.txId));
        const stillPending = pendingDonations.filter((p) => !onChainTxIds.has(p.txId));
        if (stillPending.length !== pendingDonations.length) {
            setPendingDonations(stillPending);
        }
    }, [donations, pendingDonations]);

    // Merge on-chain donations with pending (not yet confirmed) ones, dedup by txId
    const mergedDonations = useMemo(() => {
        const onChainTxIds = new Set(donations.filter((d) => d.txId).map((d) => d.txId));
        const stillPending = pendingDonations.filter((p) => !onChainTxIds.has(p.txId));
        return [...stillPending, ...donations];
    }, [donations, pendingDonations]);

    // Auto-open campaign from shared link (?campaign=<id>)
    // Re-checks on every campaign load so multiple shared links work
    const lastHandledCampaignId = useRef<string | null>(null);

    useEffect(() => {
        if (loading || campaigns.length === 0) return;

        const params = new URLSearchParams(window.location.search);
        const campaignIdStr = params.get('campaign');
        if (!campaignIdStr) return;

        // Don't re-handle the same campaign ID
        if (lastHandledCampaignId.current === campaignIdStr) return;
        lastHandledCampaignId.current = campaignIdStr;

        const match = campaigns.find((c) => c.id.toString() === campaignIdStr);
        if (match) {
            setSelectedCampaign(match);
            // Clean the URL without triggering a reload
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [campaigns, loading]);

    useScrollAnimation();

    // All campaigns = example + on-chain
    const allCampaigns: Campaign[] = [EXAMPLE_CAMPAIGN, ...campaigns];

    const handleDonate = useCallback((campaign: Campaign) => {
        setSelectedCampaign(campaign);
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedCampaign(null);
    }, []);

    const handleDonationSuccess = useCallback(async (result: DonationResult) => {
        // Add to pending so it shows in history right away with txid
        const pending: DonationRecord = {
            id: BigInt(Date.now()),
            campaignId: result.campaignId,
            donorAddress: walletHex,
            amountBTC: result.amountBTC,
            blockHeight: 0n,
            txId: result.txId,
        };
        setPendingDonations((prev) => [pending, ...prev]);
        setSelectedCampaign(null);
        await refresh();
    }, [refresh, walletHex]);

    const handleCreateSuccess = useCallback(async () => {
        setShowCreateModal(false);
        await refresh();
    }, [refresh]);

    const handleHistoryClick = useCallback(() => {
        setShowDonationHistory(true);
    }, []);

    const handleCloseHistory = useCallback(() => {
        setShowDonationHistory(false);
    }, []);

    const handleMyCampaignsClick = useCallback(() => {
        setShowMyCampaigns(true);
    }, []);

    const handleCloseMyCampaigns = useCallback(() => {
        setShowMyCampaigns(false);
    }, []);

    const handleCreateClick = useCallback(() => {
        setShowCreateModal(true);
    }, []);

    const handleCloseCreate = useCallback(() => {
        setShowCreateModal(false);
    }, []);

    return (
        <div className="app">
            <div className="grid-bg" />
            <Navbar onHistoryClick={handleHistoryClick} onMyCampaignsClick={handleMyCampaignsClick} />
            <Hero onCreateClick={handleCreateClick} />
            <StatsBar campaigns={allCampaigns} totalRaisedBTC={totalRaisedBTC} />
            <CampaignGrid
                campaigns={allCampaigns}
                onDonate={handleDonate}
                onCreateClick={handleCreateClick}
                loading={loading}
            />
            <HowItWorks />
            <CTASection onCreateClick={handleCreateClick} />
            <Footer />

            {selectedCampaign && (
                <DonateModal
                    campaign={selectedCampaign}
                    onClose={handleCloseModal}
                    onSuccess={handleDonationSuccess}
                />
            )}

            {showCreateModal && (
                <CreateCampaignModal
                    onClose={handleCloseCreate}
                    onSuccess={handleCreateSuccess}
                />
            )}

            {showDonationHistory && (
                <DonationHistory
                    donations={mergedDonations}
                    campaigns={campaigns}
                    walletHex={walletHex}
                    onClose={handleCloseHistory}
                />
            )}

            {showMyCampaigns && (
                <MyCampaigns
                    campaigns={campaigns}
                    donations={mergedDonations}
                    walletHex={walletHex}
                    onClose={handleCloseMyCampaigns}
                    onRefresh={refresh}
                />
            )}
        </div>
    );
}
